/*
 * FSK · Find Your Mushroom quiz.
 * Spec: content-drafts/website-preplan/copy-final/quiz.md
 * Result card + experience question: quiz/HANDOFF.md (2026-10-07, L-quiz-build).
 *
 * Progressive enhancement: the section server-renders the full goal→product mapping. This file
 * hides that block and drives the interactive flow in its place. Anything that throws leaves the
 * static mapping on screen, so a failure degrades to a working page rather than a blank one.
 *
 * Vanilla, no framework, no dependency — the payload has to survive a farmers-market 4G signal.
 * The one thing it leans on is Horizon's own product form (assets/product-form.js): the result
 * card clones a server-rendered <product-form-component> and lets Horizon post it, so the cart
 * drawer, the header count and the screen-reader announcement all behave exactly as on a
 * product page.
 */
(function () {
  'use strict';

  var root = document.querySelector('.fsk-quiz');
  if (!root) return;

  var app = root.querySelector('[data-quiz-app]');
  var staticBlock = root.querySelector('[data-quiz-static]');
  var dataEl = document.querySelector('[data-quiz-data]');
  if (!app || !dataEl) return;

  var DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (e) {
    return; // static mapping stays visible
  }
  var SETTINGS = DATA.settings || {};

  // ── Model ────────────────────────────────────────────────────────────────
  // Card labels are routing categories, not efficacy claims. No disease or symptom words.
  // Icon names resolve to symbols in snippets/fsk-svg-defs.liquid. 2026-10-07: the first six
  // names here used to point at symbols that never existed (focus, calm, immune, saute, dropper,
  // water), so those cards rendered an empty 28px box. Mapped to the real sprite ids.
  var GOALS = [
    { id: 'focus',    label: 'Focus',             gloss: 'Clear-headed, focused days.',                    icon: 'benefit-focus-clarity',   echo: 'focus',                  product: 'lm' },
    { id: 'rest',     label: 'Rest & calm',       gloss: 'Wind down in the evening.',                      icon: 'benefit-calm-rest',       echo: 'rest',                   product: 'reishi' },
    { id: 'immune',   label: 'Immune & gut',      gloss: 'Everyday gut and immune support.',               icon: 'benefit-immune-support',  echo: 'immune and gut support', product: 'trifecta' },
    { id: 'recovery', label: 'Recovery',          gloss: 'Bounce back after training and physical strain.', icon: 'benefit-endurance',      echo: 'recovery',               product: 'trifecta' },
    { id: 'everyday', label: 'Everyday support',  gloss: 'A steady routine, morning and night.',           icon: 'calendar',                echo: 'everyday support',       product: 'daynight' },
    { id: 'cooking',  label: 'Cooking (fresh mushrooms)', gloss: 'Fresh mushrooms to cook with. Market pickup only.', icon: 'usage-saute', echo: 'fresh mushrooms to cook with', product: null }
  ];

  var TAKES = [
    { id: 'under_tongue', label: 'Under the tongue', gloss: 'Hold 1ml under your tongue for 30 to 60 seconds. Gets in the most directly.', icon: 'usage-sublingual' },
    { id: 'in_drink',     label: 'In a drink',       gloss: "Stir 1ml into cool water or any cold drink. Easy, and it tastes good.", icon: 'usage-water' }
  ];

  // Q3 (2026-10-07). Asked on the extract and combined paths only, never on the fresh-only path.
  // The answer changes one line on the result, never the product. Off-switch: section setting
  // ask_experience.
  var EXPERIENCE = [
    { id: 'first',   label: 'No, this is my first',          gloss: '',                      icon: 'bottle-counter' },
    { id: 'powder',  label: "I've tried powders or coffees", gloss: "This one's a liquid.", icon: 'usage-coffee' },
    { id: 'already', label: 'Yes, I take them already',      gloss: '',                      icon: 'check-circle' }
  ];

  // ── v2 (2026-10-07, Andrew N1 b / N2 b): the reader rates their own day on 1 to 5 scales. ──
  // A rating is a fact the reader supplies; no question or scale end carries a symptom word.
  // ⛔ Rating VALUES never leave the browser tab: not in the page address, not in Clarity tags,
  // not in dataLayer events, not in any email property. Only the derived goals and result do.
  // Values are kept in sessionStorage so Back from a product page and "Change answers" work.
  var PATHS = [
    { id: 'extract', label: 'An extract to take every day', gloss: 'Alcohol-free liquid, by the dropper. Ships to your door.' },
    { id: 'fresh',   label: 'Fresh mushrooms to cook',      gloss: 'Market pickup only.' },
    { id: 'both',    label: 'Both',                         gloss: 'An extract shipped to you, plus fresh mushrooms for market pickup.' }
  ];
  var RATINGS = [
    { id: 'wake', group: 'Sleep',     q: 'How rested do you feel when you wake up?',     lo: 'Not rested',      hi: 'Fully rested', answer: 'waking up' },
    { id: 'wind', group: 'Sleep',     q: 'How easily do you wind down at night?',        lo: 'Not easily',      hi: 'Easily',       answer: 'winding down' },
    { id: 'fm',   group: 'Focus',     q: "How's your focus first thing in the morning?", lo: 'Low',             hi: 'High',         answer: 'morning focus' },
    { id: 'fd',   group: 'Focus',     q: "How's your focus mid-day?",                    lo: 'Low',             hi: 'High',         answer: 'mid-day focus' },
    { id: 'fl',   group: 'Focus',     q: "How's your focus after lunch?",                lo: 'Low',             hi: 'High',         answer: 'after-lunch focus' },
    { id: 'dig',  group: 'Digestion', q: "How's your digestion?",                        lo: 'Could be better', hi: 'Good',         answer: 'digestion' }
  ];
  var ADDONS = [
    { id: 'immune',   label: 'Immune support', gloss: '' },
    { id: 'recovery', label: 'Recovery',       gloss: 'Bounce back after training and physical strain.' }
  ];
  var RATING_STEPS = 9; // path + six ratings + extras + take (take is skipped for Trifecta alone)

  // Ratings -> goals. Design and every-combination check: quiz/design/rating-enum.mjs
  // (62,500 combinations per path, 0 failures). The ruled resolver below is unchanged.
  function goalsFromRatings(r, addons) {
    var focusLow = Math.min(r.fm, r.fd, r.fl), restLow = Math.min(r.wake, r.wind), g = [];
    if (focusLow <= 2) g.push('focus');                 // step 1: a 1 or 2 flags its area
    if (restLow <= 2) g.push('rest');
    if (r.dig <= 2 || has(addons, 'immune')) g.push('immune');
    if (has(addons, 'recovery')) g.push('recovery');
    if (!g.length) {                                    // step 2: nothing low, the lowest 3 counts
      if (Math.min(focusLow, restLow, r.dig) === 3) {
        if (restLow === 3) g.push('rest');              // sleep first, the booth opener
        if (focusLow === 3) g.push('focus');            // a tie with sleep = both = Day & Night
        if (!g.length) g.push('immune');                // digestion only when it alone is the 3
      }
    }
    if (!g.length) {                                    // step 3: all 4s and 5s, nothing ticked
      // Red team 2026-10-07 (5 of 8 outside seats): handing a two-bottle stack to someone who
      // rated every part of their day well invents a need. One bottle, for their lowest area.
      var low = Math.min(restLow, focusLow, r.dig);
      g.push(restLow === low ? 'rest' : focusLow === low ? 'focus' : 'immune');
    }
    return g;
  }
  function allGood() {
    if (!ratingsComplete() || state.addons.length) return false;
    return RATINGS.every(function (d) { return state.r[d.id] >= 4; });
  }
  function ratingsComplete() {
    return RATINGS.every(function (d) { return state.r[d.id] >= 1 && state.r[d.id] <= 5; });
  }
  function applyRatings() {
    if (!ratingsComplete()) return;
    state.goals = goalsFromRatings(state.r, state.addons);
    if (state.path === 'both') state.goals.push('cooking');
  }
  var STORE_KEY = 'fskQuizV2';
  function remember() {
    try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify({ path: state.path, r: state.r, addons: state.addons, goals: state.goals, take: state.take, job: state.job, flavor: state.flavor })); } catch (e) { /* storage blocked: Back simply starts fresh */ }
  }
  function recall() {
    try { return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; }
  }

  var JOBS = [
    { id: 'replace',  label: 'Replace the meat',            serving: 'sear or dry-fry it like a steak' },
    { id: 'side',     label: 'A savory side',               serving: 'sauté it in butter as a side' },
    { id: 'addition', label: 'A meaty addition to a dish',  serving: 'sauté it into pastas, tacos, and stir-fries' }
  ];

  var FLAVORS = [
    { id: 'meaty', label: 'Mild, savory flavor with a seriously meaty texture', fresh: 'lm' },
    { id: 'rich',  label: 'Balanced: rich, meaty texture and flavor',           fresh: 'oyster' },
    { id: 'umami', label: 'Deep umami, classic mushroom character',             fresh: 'pioppino' }
  ];

  // Time of day per product (product facts: the PDPs and Andrew's own routine, EXP-USE-5).
  // Occasion words only. Never a reason it helps.
  var TIME = {
    lm: 'First thing in the morning.',
    reishi: 'Night, before bed.',
    trifecta: 'Any time of day.', // FACTS-PACK 2026-08-24: Trifecta has no ruled time of day; 'with your first meal' is dead
    daynight: 'Morning and night.'
  };

  // Andrew's own routine, EXP-USE-5 (answered interview slot), trimmed to the occasion.
  var MINE = {
    lm: 'How I take mine: every single morning, alongside a little bit of caffeine.',
    reishi: 'How I take mine: every single night, right before bed.',
    trifecta: 'How I take mine: on harder farm days and market days.',
    // daynight: removed 2026-10-07. Andrew's approved farmer's note for Day & Night says the same
    // sentence, and the two printed one above the other.
  };

  // Farmer's notes (Bank 11 EXP-QUIZ-1, 2, 3, 5), approved by Andrew 2026-10-07 21:24 ET in the
  // quiz decisions session ("D4: add in those farmers notes"), from his booth tapes; the Lion's Mane
  // and Trifecta lines name their mushroom so a note can never read as another card's (claims check).
  // EXP-QUIZ-4 (Trifecta for Recovery) is unanswered: a Recovery-only result shows no note.
  // Keyed by product; rendered once per screen, INSIDE the primary product's own card, above the
  // FDA line, never under a fresh card.
  var NOTES = {
    lm: "Lion's Mane's my favorite one. I take it every morning, that's how I get my day started.",
    reishi: 'Reishi I have the most reverence for. Sleep is very underrated.',
    trifecta: "Trifecta's the one I recommend for gut health and overall bodily support.",
    daynight: "Lion's Mane in the morning and Reishi at night, that's my own personal favorite stack."
  };
  var NOTE_PRIORITY = ['rest', 'focus', 'immune', 'recovery', 'everyday'];

  var state = { goals: [], take: null, exp: null, job: null, flavor: null, screen: 'p', path: null, r: {}, addons: [], sent: false };

  // ── Resolver (§1, operates on the deduped PRODUCT set so equivalent sets always match) ──
  function resolveProducts(goalIds) {
    var set = [];
    goalIds.forEach(function (g) {
      var goal = byId(GOALS, g);
      if (goal && goal.product && set.indexOf(goal.product) === -1) set.push(goal.product);
    });

    // The old exact-match collapse ({LM, Reishi, Trifecta} -> Trifecta alone) is REMOVED.
    // RULED by Andrew 2026-07-20: "Focus should not collapse into Trifecta. Focus is purely
    // Lion's Mane." Trifecta is one blended bottle, so collapsing to it left a shopper who
    // asked for Focus with no dedicated Lion's Mane. This supersedes the 2026-07-13 ruling
    // that made Trifecta the one-bottle answer for {Focus, Rest, Immune}.
    //
    // Day & Night absorption below is deliberately KEPT: it is a two-bottle stack that
    // physically contains a Lion's Mane bottle, so Focus is still honored in full.

    // Absorption: a named stack replaces its components, then components are dropped.
    if (has(set, 'lm') && has(set, 'reishi')) {
      set = set.filter(function (p) { return p !== 'lm' && p !== 'reishi'; });
      if (!has(set, 'daynight')) set.push('daynight');
    }
    if (has(set, 'daynight')) {
      set = set.filter(function (p) { return p !== 'lm' && p !== 'reishi'; });
    }
    return set;
  }

  // Rest and Focus are carried by Day & Night when it absorbs Lion's Mane and Reishi, so sleep
  // still comes first in the note priority. Trifecta's note is for Immune & gut only.
  function primaryProduct(goalIds, products) {
    for (var i = 0; i < NOTE_PRIORITY.length; i++) {
      var goal = byId(GOALS, NOTE_PRIORITY[i]);
      if (!goal || !has(goalIds, goal.id)) continue;
      var prod = goal.product;
      if ((prod === 'lm' || prod === 'reishi') && has(products, 'daynight')) prod = 'daynight';
      if (prod === 'trifecta' && !has(goalIds, 'immune')) continue;
      if (has(products, prod)) return prod;
    }
    return products[0];
  }
  function noteKey(goalIds, products) {
    var key = primaryProduct(goalIds, products);
    if (key === 'trifecta' && !has(goalIds, 'immune')) return null;   // fallback products[0]
    return key && NOTES[key] ? key : null;
  }

  function benefitGoals() {
    return state.goals.filter(function (g) { return g !== 'cooking'; });
  }
  function path() {
    var b = benefitGoals().length, c = has(state.goals, 'cooking');
    if (b && c) return 'combined';
    if (c) return 'fresh';
    return 'extract';
  }
  function askExperience() {
    return SETTINGS.askExperience !== false && path() !== 'fresh';
  }

  // ── Copy helpers ─────────────────────────────────────────────────────────
  function joinList(items) {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    // "immune and gut support" already carries an "and", so an "and" join would stutter
    // ("focus and immune and gut support"). Comma-join the whole list in that case.
    if (items.some(function (i) { return i.indexOf(' and ') !== -1; })) return items.join(', ');
    if (items.length === 2) return items[0] + ' and ' + items[1];
    return items.slice(0, -1).join(', ') + ', and ' + items[items.length - 1];
  }
  function countWord(n) { return n === 1 ? 'one' : n === 2 ? 'pair' : 'set'; }

  function leadIn(products) {
    var echoes = benefitGoals().map(function (g) { return byId(GOALS, g).echo; });
    if (path() === 'fresh') {
      return "You're after fresh mushrooms to cook with, so here's the one I'd hand you at the market.";
    }
    // v2: no score is read back and nothing is joined to a low rating with "so" (claims and voice
    // reviews, 2026-10-07): next to a feel line, "you rated X low, so here's Y" reads as "Y fixes X".
    var tail = path() === 'combined' ? ', plus something fresh to cook.' : '.';
    var asked = state.addons.map(function (a) { return a === 'immune' ? 'immune support' : 'recovery'; });
    if (allGood()) {
      return "You gave every answer a 4 or 5. If you'd still like one to start with, here's the one I'd reach for" + tail;
    }
    if (has(state.goals, 'everyday') && products.length === 1) {
      return "Here's the everyday pair I'd start you on, one for the morning and one for night" + tail;
    }
    var lowRated = ratingsComplete() && (Math.min(state.r.wake, state.r.wind, state.r.fm, state.r.fd, state.r.fl, state.r.dig) <= 3);
    if (asked.length && !lowRated) {
      return "You asked about " + joinList(asked) + ", so here's the " + countWord(products.length) + " I'd start you on" + tail;
    }
    if (asked.length) {
      return "From your answers, and since you asked about " + joinList(asked) + ", here's the " + countWord(products.length) + " I'd start you on" + tail;
    }
    if (!ratingsComplete()) {
      return "You're after " + joinList(echoes) + ", so here's the " + countWord(products.length) + " I'd start you on" + tail;
    }
    return "From your answers, here's the " + countWord(products.length) + " I'd start you on" + tail;
  }

  function microLabel(productKey) {
    var owning = benefitGoals().filter(function (g) {
      var goal = byId(GOALS, g);
      if (goal.product === productKey) return true;
      // Day & Night absorbs Focus and Rest.
      if (productKey === 'daynight' && (g === 'focus' || g === 'rest')) return true;
      return false;
    });
    if (!owning.length) return '';
    var labels = owning.map(function (g) { return byId(GOALS, g).label; });
    if (labels.length > 1) return 'Your ' + labels.join(' and ').replace(/Rest & calm/g, 'Rest') + ' pick';
    return 'Your ' + labels[0] + ' pick';
  }

  function usageSentence(p) {
    if (!p.sublingual) return p.usageDrink;
    return state.take === 'under_tongue' ? p.usageTongue : p.usageDrink;
  }
  // Time of day in bold, then the ruled usage sentence. Day & Night's usage sentence already
  // says "morning and evening", so the trailing clause is dropped there to avoid saying it twice.
  function routineLine(key, p) {
    var usage = usageSentence(p);
    if (key === 'daynight') usage = usage.replace(/,\s*morning and evening\./, '.');
    var ack = (!p.sublingual && state.take === 'under_tongue')
      ? ' This one goes straight into a drink rather than under the tongue.'
      : '';
    return esc(usage) + esc(ack); // time of day now rides the card label above
  }
  // The one line that changes with Q3. Facts about the bottle, never about the reader.
  function experienceLine(key, p, buy) {
    if (!state.exp) return '';
    var vs = (buy && buy.variants) || [];
    var big = vs.filter(function (v) { return v.big; })[0];
    if (state.exp === 'first') {
      // Day & Night is two bottles, morning and night, so "one a day" would contradict its own line.
      return (key === 'daynight' ? 'One of each a day' : 'One a day') + ' is what I recommend to start with. Consistency really adds up.';
    }
    if (state.exp === 'powder') {
      // Andrew's tape wording (2026-08-09 01:21:14). "Water-based" is website-safe per the guardrails
      // but the label lists glycerin first, so the narrower true line ships until he rules (pack D5).
      return "It's alcohol-free, so it's easy to take.";
    }
    if (state.exp === 'already' && big && big.available) {
      return key === 'daynight'
        ? 'The pair of 2 oz bottles is 60 servings each, two months at one a day.'
        : 'The ' + big.label + ' is ' + big.servings + ', two months at one a day.';
    }
    return '';
  }

  // Genuine-OOS only: every variant unavailable, and never for a product on the artifact
  // exclusion list (Lion's Mane storefront OOS is stale inventory data, not a real stockout).
  function isGenuinelyOOS(key, p) {
    if (p.bisExempt === true) return false;
    var buy = DATA.buy && DATA.buy[key];
    if (buy && buy.variants && buy.variants.length) {
      return !buy.variants.some(function (v) { return v.available; });
    }
    return p.available === false;
  }

  // ── Rendering ────────────────────────────────────────────────────────────
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function icon(name) {
    return '<svg aria-hidden="true"><use href="#fsk-ic-' + name + '"/></svg>';
  }
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function optionCard(opt, multi, selected) {
    var tag = multi ? 'label' : 'button';
    var n = el(tag, 'fsk-quiz__opt' + (selected ? ' is-selected' : ''));
    if (multi) {
      // The checkbox is stretched over the whole card in CSS (fsk-quiz__optinput) rather than
      // visually hidden, so every tap lands on the control itself. Relying on implicit label
      // activation left the card's padding dead to taps, which silently dropped a selection.
      n.innerHTML =
        '<input type="checkbox" class="fsk-quiz__optinput" value="' + opt.id + '"' + (selected ? ' checked' : '') + '>' +
        (opt.icon ? '<span class="fsk-quiz__opticon">' + icon(opt.icon) + '</span>' : '') +
        '<span class="fsk-quiz__optlabel">' + esc(opt.label) + '</span>' +
        (opt.gloss ? '<span class="fsk-quiz__optgloss">' + esc(opt.gloss) + '</span>' : '');
    } else {
      n.type = 'button';
      n.innerHTML =
        (opt.icon ? '<span class="fsk-quiz__opticon">' + icon(opt.icon) + '</span>' : '') +
        '<span class="fsk-quiz__optlabel">' + esc(opt.label) + '</span>' +
        (opt.gloss ? '<span class="fsk-quiz__optgloss">' + esc(opt.gloss) + '</span>' : '');
    }
    return n;
  }

  function screenShell(title, opts) {
    opts = opts || {};
    var wrap = el('div', 'fsk-glass fsk-quiz__panel');
    if (opts.back) {
      var back = el('button', 'fsk-quiz__back', '&larr; Back');
      back.type = 'button';
      back.addEventListener('click', opts.back);
      wrap.appendChild(back);
    }
    wrap.appendChild(el('h2', 'fsk-h fsk-h--md fsk-quiz__q', esc(title)));
    return wrap;
  }

  // Each step gets its own history entry (values never go in it: only the screen name), so the
  // phone Back gesture steps back through the quiz instead of leaving the page.
  var lastScreen = null, fromPop = false;
  window.addEventListener('popstate', function (e) {
    if (e.state && e.state.fskQuiz) { fromPop = true; state.screen = e.state.fskQuiz; render(); fromPop = false; }
  });
  function render() {
    if (lastScreen !== null && lastScreen !== state.screen && !fromPop && window.history && window.history.pushState) {
      window.history.pushState({ fskQuiz: state.screen }, '', window.location.href);
    }
    lastScreen = state.screen;
    app.innerHTML = '';
    var fn = { p: renderPath, extras: renderExtras, q1: renderQ1, q2: renderQ2, q3: renderQ3, q2f: renderQ2f, q3f: renderQ3f, result: renderResult }[state.screen];
    if (!fn && state.screen.indexOf('r_') === 0) fn = function () { renderRating(state.screen.slice(2)); };
    if (!fn) { state.screen = 'p'; fn = renderPath; }
    fn();
    remember();
    app.hidden = false;
    if (staticBlock) staticBlock.hidden = true;
    syncUrl();
    clarityTag('quiz_step', state.screen);
    var focusTarget = app.querySelector('h2');
    if (focusTarget) { focusTarget.setAttribute('tabindex', '-1'); focusTarget.focus({ preventScroll: true }); }
    bringIntoView();
  }

  // 2026-10-07 (Clarity, Andrew-approved fix): each step REPLACES a tall panel with a shorter
  // one, and the page kept its scroll position, so a phone visitor who scrolled down to
  // "Show me what fits" landed on empty background and the email box, with the next question
  // above the top of the screen. Six of 81 quiz visits ended right after the first answer.
  // After every step change (never on the first render, so a deep link or a fresh visit is
  // not yanked), bring the new panel's top back under the sticky header.
  var firstRender = true;
  function bringIntoView() {
    if (firstRender) { firstRender = false; return; }
    var hdr = document.querySelector('header-component, .header-wrapper, header');
    var offset = (hdr ? hdr.getBoundingClientRect().bottom : 0);
    if (offset < 0 || offset > window.innerHeight / 3) offset = 0;
    var top = app.getBoundingClientRect().top;
    if (top >= offset && top < window.innerHeight * 0.4) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var delta = top - offset - 16;
    // ⛔ Above 990px this theme locks html/body (overflow:hidden) and scrolls `.page-wrapper`
    // instead; on phones the window scrolls. Scroll whichever ancestor actually scrolls —
    // window.scrollTo alone was measured doing nothing on desktop.
    var sc = app.parentElement;
    while (sc && sc !== document.body) {
      var oy = window.getComputedStyle(sc).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && sc.scrollHeight > sc.clientHeight) break;
      sc = sc.parentElement;
    }
    if (sc && sc !== document.body) {
      sc.scrollTo({ top: Math.max(0, sc.scrollTop + delta), behavior: reduce ? 'auto' : 'smooth' });
    } else {
      window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  // ── v2 screens ───────────────────────────────────────────────────────────
  function progress(n, total) {
    total = total || RATING_STEPS;
    var w = el('div', 'fsk-quiz__progress', '<div class="fsk-quiz__bar" aria-hidden="true"><span style="width:' + Math.round(100 * n / total) + '%"></span></div>' + n + ' of ' + total);
    return w;
  }
  function stepShell(n, eyebrow, title, back) {
    var wrap = el('div', 'fsk-glass fsk-quiz__panel');
    if (back) {
      var b = el('button', 'fsk-quiz__back', '&larr; Back');
      b.type = 'button';
      b.addEventListener('click', back);
      wrap.appendChild(b);
    }
    wrap.appendChild(progress(n));
    if (eyebrow) wrap.appendChild(el('p', 'fsk-quiz__eyebrow', esc(eyebrow)));
    wrap.appendChild(el('h2', 'fsk-h fsk-h--md fsk-quiz__q', esc(title)));
    return wrap;
  }

  function renderPath() {
    var panel = stepShell(1, '', 'What are you looking for?', null);
    var grid = el('div', 'fsk-quiz__grid');
    PATHS.forEach(function (o) {
      var card = optionCard(o, false, state.path === o.id);
      card.addEventListener('click', function () {
        state.path = o.id;
        track('quiz_answer', { question: 'path', value: o.id });
        if (o.id === 'fresh') { state.goals = ['cooking']; state.screen = 'q2f'; }
        else { state.screen = 'r_' + RATINGS[0].id; }
        render();
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    panel.appendChild(el('p', 'fsk-quiz__escape2', 'Know what you want? <a class="fsk-link" href="' + DATA.urls.extracts + '">See all the extracts</a>.'));
    app.appendChild(panel);
  }

  function nextAfterRating(i) {
    return i + 1 < RATINGS.length ? 'r_' + RATINGS[i + 1].id : 'extras';
  }
  function renderRating(id) {
    var i = RATINGS.map(function (d) { return d.id; }).indexOf(id);
    if (i < 0) { state.screen = 'p'; render(); return; }
    var d = RATINGS[i];
    var panel = stepShell(i + 2, d.group, d.q, function () { state.screen = i ? 'r_' + RATINGS[i - 1].id : 'p'; render(); });
    panel.appendChild(el('p', 'fsk-quiz__cue', 'Tap 1 to 5.'));
    var fs = el('fieldset', 'fsk-quiz__rate');
    fs.setAttribute('data-clarity-mask', 'true');
    var endsId = 'fsk-quiz-ends-' + d.id;
    var html = '<legend class="fsk-visually-hidden">' + esc(d.q) + '</legend><div class="fsk-quiz__scale">';
    for (var v = 1; v <= 5; v++) {
      var lab = v === 1 ? ', ' + d.lo : v === 5 ? ', ' + d.hi : '';
      html += '<label class="fsk-quiz__pip"><input type="radio" name="fsk-quiz-' + d.id + '" value="' + v + '"' + (state.r[d.id] === v ? ' checked' : '') +
        ' aria-label="' + v + esc(lab) + '" aria-describedby="' + endsId + '"><span aria-hidden="true">' + v + '</span></label>';
    }
    html += '</div><div class="fsk-quiz__ends" id="' + endsId + '"><span>1 · ' + esc(d.lo) + '</span><span>5 · ' + esc(d.hi) + '</span></div>';
    fs.innerHTML = html;
    panel.appendChild(fs);
    var next = el('button', 'fsk-btn fsk-btn--primary fsk-quiz__next', 'Next');
    next.type = 'button';
    next.disabled = !state.r[d.id];
    function go() {
      if (!state.r[d.id]) return;
      if (state.returnTo === 'result') { state.returnTo = null; applyRatings(); state.screen = 'result'; }
      else state.screen = nextAfterRating(i);
      render();
    }
    next.addEventListener('click', go);
    // A choice only selects; Next moves on. Red team 2026-10-07 (5 of 8 outside seats): an
    // auto-advance moves a mis-tap or a screen-reader user on before they can check the answer.
    fs.addEventListener('change', function (e) {
      var v = parseInt(e.target.value, 10);
      if (!(v >= 1 && v <= 5)) return;
      state.r[d.id] = v;
      remember();
      next.disabled = false;
      track('quiz_answer', { question: d.id }); // ⛔ no value: ratings stay in the tab
    });
    panel.appendChild(next);
    app.appendChild(panel);
  }

  function renderExtras() {
    var panel = stepShell(RATINGS.length + 2, '', "Anything else you're after?", function () { state.screen = 'r_' + RATINGS[RATINGS.length - 1].id; render(); });
    panel.appendChild(el('p', 'fsk-quiz__cue', 'Pick any, or none.'));
    var grid = el('div', 'fsk-quiz__grid');
    ADDONS.forEach(function (a) {
      var card = optionCard(a, true, has(state.addons, a.id));
      card.querySelector('input').addEventListener('change', function () {
        if (this.checked) { if (!has(state.addons, a.id)) state.addons.push(a.id); }
        else state.addons = state.addons.filter(function (x) { return x !== a.id; });
        card.classList.toggle('is-selected', this.checked);
        remember();
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    var next = el('button', 'fsk-btn fsk-btn--primary fsk-quiz__next', 'Next');
    next.type = 'button';
    next.addEventListener('click', function () {
      track('quiz_answer', { question: 'extras', value: state.addons.join(',') });
      applyRatings();
      var prods = resolveProducts(state.goals);
      // Trifecta always goes in a drink, so asking how to take it would be a wasted screen.
      state.screen = (prods.length === 1 && prods[0] === 'trifecta') ? 'result' : 'q2';
      render();
    });
    panel.appendChild(next);
    app.appendChild(panel);
  }

  function renderQ1() {
    var panel = screenShell('What are you after?');
    var grid = el('div', 'fsk-quiz__grid');
    GOALS.forEach(function (g) {
      var card = optionCard(g, true, has(state.goals, g.id));
      card.querySelector('input').addEventListener('change', function () {
        if (this.checked) { if (!has(state.goals, g.id)) state.goals.push(g.id); }
        else state.goals = state.goals.filter(function (x) { return x !== g.id; });
        card.classList.toggle('is-selected', this.checked);
        cont.disabled = state.goals.length === 0;
        track('quiz_answer', { question: 'q1', value: state.goals.join(',') });
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    panel.appendChild(el('p', 'fsk-quiz__cue', "Pick as many as fit you and I'll build the set."));

    var cont = el('button', 'fsk-btn fsk-btn--primary fsk-quiz__continue', 'Show me what fits ' + icon('arrow'));
    cont.type = 'button';
    cont.disabled = state.goals.length === 0;
    cont.addEventListener('click', function () {
      if (!state.goals.length) return;
      state.screen = (path() === 'fresh') ? 'q2f' : 'q2';
      render();
    });
    panel.appendChild(cont);
    app.appendChild(panel);
  }

  function renderQ2() {
    var panel = screenShell('How do you want to take it?', { back: function () { state.screen = ratingsComplete() ? 'extras' : 'p'; render(); } });
    if (ratingsComplete()) panel.insertBefore(progress(RATING_STEPS), panel.querySelector('h2'));
    var grid = el('div', 'fsk-quiz__grid fsk-quiz__grid--two');
    TAKES.forEach(function (t) {
      var card = optionCard(t, false, state.take === t.id);
      card.addEventListener('click', function () {
        state.take = t.id;
        track('quiz_answer', { question: 'q2', value: t.id });
        state.screen = askExperience() ? 'q3' : 'result';
        render();
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    panel.appendChild(el('p', 'fsk-quiz__cue', 'Both work. Under the tongue gets in a little more directly; a drink is the easy way, and it tastes good.'));
    app.appendChild(panel);
  }

  function renderQ3() {
    var panel = screenShell('Have you tried mushroom extracts before?', { back: function () { state.screen = 'q2'; render(); } });
    var grid = el('div', 'fsk-quiz__grid fsk-quiz__grid--three');
    EXPERIENCE.forEach(function (x) {
      var card = optionCard(x, false, state.exp === x.id);
      card.addEventListener('click', function () {
        state.exp = x.id;
        track('quiz_answer', { question: 'q3', value: x.id });
        state.screen = 'result';
        render();
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    panel.appendChild(el('p', 'fsk-quiz__cue', 'Last one.'));
    app.appendChild(panel);
  }

  function renderQ2f() {
    var panel = screenShell('What is the job in the kitchen?', { back: function () { state.screen = 'p'; render(); } });
    panel.insertBefore(progress(2, 3), panel.querySelector('h2'));
    var grid = el('div', 'fsk-quiz__grid fsk-quiz__grid--three');
    JOBS.forEach(function (j) {
      var card = optionCard(j, false, state.job === j.id);
      card.addEventListener('click', function () {
        state.job = j.id;
        track('quiz_answer', { question: 'q2f', value: j.id });
        state.screen = 'q3f';
        render();
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    app.appendChild(panel);
  }

  function renderQ3f() {
    var panel = screenShell('What flavor and texture are you after?', { back: function () { state.screen = 'q2f'; render(); } });
    panel.insertBefore(progress(3, 3), panel.querySelector('h2'));
    var grid = el('div', 'fsk-quiz__grid fsk-quiz__grid--three');
    FLAVORS.forEach(function (f) {
      var card = optionCard(f, false, state.flavor === f.id);
      card.addEventListener('click', function () {
        state.flavor = f.id;
        track('quiz_answer', { question: 'q3f', value: f.id });
        state.screen = 'result';
        render();
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    app.appendChild(panel);
  }

  // ── Result: the buy block ────────────────────────────────────────────────
  // Clones the server-rendered Horizon product form for this product, builds the size pills
  // from the data island, and keeps the form's hidden `id` and `selling_plan` inputs in step
  // with the radios. Prices are the Liquid money strings, copied into text, never computed.
  // Returns null when nothing can be bought (no template, no variants, nothing available), and
  // the caller decides between back-in-stock and a plain link.
  function buyBlock(key) {
    var tpl = document.querySelector('[data-quiz-buy="' + key + '"]');
    var buy = DATA.buy && DATA.buy[key];
    if (!tpl || !tpl.content || !buy || !buy.variants || !buy.variants.length) return null;
    var available = buy.variants.filter(function (v) { return v.available; });
    if (!available.length) return null;

    var frag = tpl.content.cloneNode(true);
    var block = frag.querySelector('[data-quiz-buy-root]');
    if (!block) return null;
    var sizes = block.querySelector('[data-quiz-sizes]');
    var plans = block.querySelector('[data-quiz-plans]');
    var idInput = block.querySelector('input[name="id"]');
    var planInput = block.querySelector('[data-quiz-selling-plan]');
    var subRadio = plans.querySelector('input[value="subscribe"]');
    var oneRadio = plans.querySelector('input[value="onetime"]');
    if (!sizes || !plans || !idInput || !planInput || !subRadio || !oneRadio) return null;
    setField(block, 'goals', state.goals.join(','));

    // The PDP's own default size (selected_or_first_available_variant), if it is in stock.
    var pick = available.filter(function (v) { return String(v.id) === String(buy.selected); })[0] || available[0];

    buy.variants.forEach(function (v) {
      var lab = el('label', 'fsk-quiz__size');
      lab.innerHTML =
        '<input type="radio" name="fsk-quiz-size-' + key + '" value="' + esc(v.id) + '" data-quiz-size' +
          (v.available ? '' : ' disabled') + (String(v.id) === String(pick.id) ? ' checked' : '') + '>' +
        (v.big && v.available ? '<span class="fsk-quiz__rec">Recommended</span>' : '') +
        '<span class="fsk-quiz__sizerow"><span>' + esc(v.label) + '</span><span>' + esc(v.price) + '</span></span>' +
        '<small>' + esc(v.servings) + (v.available ? '' : ' <span class="fsk-quiz__soldout">Sold out</span>') + '</small>';
      sizes.appendChild(lab);
    });

    function current() {
      var checked = sizes.querySelector('input:checked');
      return buy.variants.filter(function (v) { return checked && String(v.id) === String(checked.value); })[0] || pick;
    }
    function planFor(v) {
      // Cadence follows the bottle (D12): a 2 oz is 60 servings, so it defaults to every 60 days.
      var pref = v.big ? v.plans.bimonthly : v.plans.monthly;
      var alt = v.big ? v.plans.monthly : v.plans.bimonthly;
      if (pref && pref.id) return { plan: pref, bi: v.big };
      if (alt && alt.id) return { plan: alt, bi: !v.big };
      return null;
    }
    function paint() {
      var v = current();
      var pf = planFor(v);
      block.querySelector('[data-quiz-price-onetime]').textContent = v.price;
      if (pf) {
        plans.removeAttribute('data-no-plans');
        block.querySelector('[data-quiz-price-sub]').textContent = pf.plan.price;
        block.querySelector('[data-quiz-cadence]').textContent = (pf.bi ? 'Every 2 months' : 'Every month') + ' · pause or skip';
      } else {
        plans.setAttribute('data-no-plans', '');
        if (subRadio.checked) oneRadio.checked = true;
      }
      idInput.value = v.id;
      // Empty string = one-time. Horizon posts whatever is in the form, so this field is the
      // single authority on whether a plan is attached.
      planInput.value = (pf && subRadio.checked) ? pf.plan.id : '';
    }
    block.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !(t.hasAttribute('data-quiz-size') || t.hasAttribute('data-quiz-plan'))) return;
      paint();
      track('quiz_answer', { question: t.hasAttribute('data-quiz-size') ? 'size' : 'plan', product: key, value: t.value });
    });
    var atc = block.querySelector('[data-quiz-atc]');
    if (atc) {
      atc.addEventListener('click', function () {
        track('quiz_cta', { product: key, action: 'add_to_cart', plan: planInput.value ? 'subscribe' : 'onetime', variant: idInput.value });
      });
    }
    var pfc = block.querySelector('product-form-component');
    if (pfc) {
      // Horizon's own cart event, with its promise: the add counts only when it succeeded.
      pfc.addEventListener('shopify:cart:lines-update', function (e) {
        var pr = e && e.promise;
        if (!pr || typeof pr.then !== 'function') return;
        pr.then(function (r) {
          if (r && r.detail && r.detail.didError) return;
          track('quiz_atc_done', { product: key, plan: planInput.value ? 'subscribe' : 'onetime', variant: idInput.value });
          clarityTag('quiz_atc', key);
        }, function () {});
      });
    }
    paint();
    return block;
  }

  // ── Result: cards ────────────────────────────────────────────────────────
  function productImage(buyish, fallbackAlt) {
    if (!buyish || !buyish.image) return '';
    return '<img src="' + esc(buyish.image) + '" alt="' + esc(buyish.imageAlt || fallbackAlt || '') + '" width="480" height="600" loading="lazy">';
  }

  function extractCard(key) {
    var p = DATA.products[key];
    if (!p) return null;
    var buy = DATA.buy && DATA.buy[key];
    var stacked = SETTINGS.cardLayout === 'stacked';
    var card = el('article', 'fsk-quiz__card fsk-quiz__card--buy' + (stacked ? ' is-stacked' : ''));
    var oos = isGenuinelyOOS(key, p);
    // v2: the card label is the product's ruled time of day (Andrew routes by time at the booth).
    var micro = (TIME[key] || '').replace(/\.$/, '') || microLabel(key) || 'Your pick';
    var titleHtml = '<a class="fsk-link" href="' + esc(p.url) + refParam() + '">' + esc(p.title) + '</a>';
    var img = productImage(buy, p.title);

    if (stacked) {
      if (img) card.appendChild(el('div', 'fsk-quiz__media--full', img));
      var sh = el('div', 'fsk-quiz__buybody');
      sh.appendChild(el('p', 'fsk-quiz__micro-label', esc(micro)));
      sh.appendChild(el('h3', 'fsk-quiz__cardtitle', titleHtml));
      card.appendChild(sh);
    } else {
      var head = el('div', 'fsk-quiz__buyhead');
      if (img) head.appendChild(el('div', 'fsk-quiz__media--thumb', img));
      var hb = el('div', '');
      hb.appendChild(el('p', 'fsk-quiz__micro-label', esc(micro)));
      hb.appendChild(el('h3', 'fsk-quiz__cardtitle', titleHtml));
      head.appendChild(hb);
      card.appendChild(head);
    }

    var body = el('div', 'fsk-quiz__buybody');
    if (cameFrom(p.url)) {
      body.appendChild(el('p', 'fsk-quiz__affirm', "This is the one. Here's why it fits what you told me."));
    }
    body.appendChild(el('p', 'fsk-quiz__benefit', esc(p.benefit)));

    // Reishi + Cooking: point at the fresh Oyster card already on this screen, never send away.
    if (key === 'reishi' && has(state.goals, 'cooking') && !has(state.goals, 'focus')) {
      body.appendChild(el('p', 'fsk-quiz__conditional',
        "Reishi is a medicinal mushroom, and we never sell it fresh. If you want something to cook with, the fresh Oyster below is the one I'd hand you."));
    }

    var block = oos ? null : buyBlock(key);
    if (block) {
      body.appendChild(block);
      card.setAttribute('data-quiz-buyable', '');
    } else if (oos) {
      body.appendChild(formFor('bis', p.title));
    } else {
      // No buy template or nothing purchasable through it: today's link, never a dead button.
      var cta = el('a', 'fsk-btn fsk-btn--primary', 'Shop the ' + esc(p.title) + ' ' + icon('arrow'));
      cta.href = p.url + refParam();
      cta.addEventListener('click', function () { track('quiz_cta', { product: p.key }); });
      body.appendChild(cta);
    }

    if (block) body.appendChild(el('p', 'fsk-quiz__ship', 'Free shipping on orders of $40 or more.'));
    if (p.feel) body.appendChild(el('p', 'fsk-quiz__feel', esc(p.feel)));
    body.appendChild(el('p', 'fsk-quiz__usage', routineLine(key, p)));
    if (MINE[key]) body.appendChild(el('p', 'fsk-quiz__mine', esc(MINE[key])));
    var ex = experienceLine(key, p, buy);
    if (ex) body.appendChild(el('p', 'fsk-quiz__exp', esc(ex)));
    if (state.noteKey === key) body.appendChild(el('blockquote', 'fsk-quiz__note', esc(NOTES[key])));
    body.appendChild(el('p', 'fsk-quiz__pdp', '<a class="fsk-link" href="' + esc(p.url) + refParam() + '">See the full product page</a>'));
    body.appendChild(el('p', 'fsk-quiz__dshea', '<em>' + esc(DATA.dshea) + '</em>'));
    card.appendChild(body);
    return card;
  }

  function freshCard(key) {
    var f = DATA.fresh[key];
    if (!f) return null;
    var card = el('article', 'fsk-quiz__card fsk-quiz__card--fresh');
    var img = productImage(f, f.title);
    if (img) card.appendChild(el('div', 'fsk-quiz__media--fresh', img));
    card.appendChild(el('h3', 'fsk-quiz__cardtitle',
      f.booth ? esc(f.title) : '<a class="fsk-link" href="' + esc(f.url) + refParam() + '">' + esc(f.title) + '</a>'));
    card.appendChild(el('p', 'fsk-quiz__benefit', esc(f.flavor)));
    card.appendChild(el('p', 'fsk-quiz__feel', esc(f.storage)));

    if (state.job) {
      var job = byId(JOBS, state.job);
      if (job) card.appendChild(el('p', 'fsk-quiz__usage', 'Serving: ' + esc(job.serving) + '.'));
    }

    if (f.booth) {
      var boothCta = el('a', 'fsk-btn fsk-btn--primary', esc(f.cta) + ' ' + icon('arrow'));
      boothCta.href = DATA.urls.markets + refParam();
      card.appendChild(boothCta);
    } else {
      var reserve = el('button', 'fsk-btn fsk-btn--primary', esc(f.cta));
      reserve.type = 'button';
      reserve.addEventListener('click', function () {
        if (card.querySelector('[data-market-form]')) return;
        var wrap = el('div', '');
        wrap.setAttribute('data-market-form', '');
        wrap.appendChild(formFor('market', f.title));
        card.appendChild(wrap);
        track('quiz_cta', { product: f.key, action: 'market_reminder' });
      });
      card.appendChild(reserve);
    }
    // Fresh is culinary only. No health claim, so no DSHEA on this card.
    return card;
  }

  function renderResult() {
    var panel = el('div', 'fsk-quiz__panel fsk-quiz__panel--result');
    var p = path();
    var products = p === 'fresh' ? [] : resolveProducts(state.goals);
    state.noteKey = products.length ? noteKey(state.goals, products) : null;

    panel.appendChild(el('p', 'fsk-quiz__leadin', esc(leadIn(products))));

    if (products.length > 1) {
      panel.appendChild(el('h2', 'fsk-h fsk-h--md', 'Your stack'));
    } else {
      panel.appendChild(el('h2', 'fsk-visually-hidden', 'Your result'));
    }

    var cards = el('div', 'fsk-quiz__cards');
    // The product for the shopper's first-priority answer leads (sleep, then focus, then gut).
    var lead = primaryProduct(state.goals, products);
    var ordered = [lead].concat(products.filter(function (k) { return k !== lead; }));
    ordered.forEach(function (key, n) {
      var c = extractCard(key);
      if (!c) return;
      if (n === 0 || ordered.length < 2) { cards.appendChild(c); return; }
      var d = el('details', 'fsk-quiz__also');
      var p2 = DATA.products[key];
      d.appendChild(el('summary', 'fsk-quiz__alsosum', 'Also in your pair: <b>' + esc(p2.title) + '</b> <span>' + esc((TIME[key] || '').replace(/\.$/, '')) + '</span>'));
      d.appendChild(c);
      cards.appendChild(d);
    });

    // Path B: the chosen fresh mushroom. Path C: one fresh secondary, no fresh sub-questions.
    if (p === 'fresh') {
      var chosen = state.flavor ? byId(FLAVORS, state.flavor).fresh : 'lm';
      var fc = freshCard(chosen);
      if (fc) cards.appendChild(fc);
    } else if (p === 'combined') {
      var secondary = has(state.goals, 'focus') ? 'lm' : 'oyster';
      var sc = freshCard(secondary);
      if (sc) { sc.classList.add('is-secondary'); cards.appendChild(sc); }
    }
    panel.appendChild(cards);

    // Farmer's note: rendered inside its own product's card by extractCard (state.noteKey).

    // The reader's own numbers, read back as they gave them, with no interpretation and no colour.
    if (p !== 'fresh' && ratingsComplete()) {
      var parts = RATINGS.map(function (d) {
        return '<button type="button" class="fsk-quiz__ansbtn" data-quiz-edit="' + d.id + '" aria-label="Change ' + esc(d.answer) + '">' + esc(d.answer) + ' <b>' + state.r[d.id] + '</b></button>';
      });
      if (state.addons.length) parts.push(state.addons.map(function (a) { return a === 'immune' ? 'immune support' : 'recovery'; }).join(', '));
      var ans = el('p', 'fsk-quiz__answersline', 'Your answers: ' + parts.join(' &middot; '));
      ans.setAttribute('data-clarity-mask', 'true'); // recordings must not carry the ratings
      ans.addEventListener('click', function (e) {
        var b = e.target.closest('[data-quiz-edit]');
        if (!b) return;
        state.returnTo = 'result'; state.screen = 'r_' + b.getAttribute('data-quiz-edit'); render();
      });
      panel.appendChild(ans);
      var chg = el('button', 'fsk-quiz__changebtn', 'Change answers');
      chg.type = 'button';
      chg.addEventListener('click', function () { state.returnTo = null; state.screen = 'r_' + RATINGS[0].id; render(); });
      panel.appendChild(chg);
    }

    // Two-card results: each card has its own Add to cart. The old /cart/ permalink is gone
    // (it left the page and ignored the purchase option) and no combined button replaces it:
    // the conversion review found a third button more than two screens down, with no total it
    // could honestly show, worth less than the measurement noise it would add.

    if (p === 'fresh' || p === 'combined') {
      panel.appendChild(el('p', 'fsk-quiz__bridge',
        'Fresh is market pickup at our Central Florida markets. Not nearby? Our extracts are available online, anywhere &rarr; ' +
        '<a class="fsk-link" href="' + DATA.urls.extracts + '">see all the extracts</a>.'));
    }

    // Email capture renders only after a result. Fresh-only results get the recipe capture;
    // the extract "your result, saved" block (which promises the usage guide and mocktail
    // e-book) belongs to extract and combined screens, not to a purely culinary one.
    if (p === 'fresh') {
      var chosenFresh = state.flavor ? byId(FLAVORS, state.flavor).fresh : 'lm';
      panel.appendChild(formFor('recipes', DATA.fresh[chosenFresh].title));
    } else {
      panel.appendChild(formFor('result', products.map(function (k) { return DATA.products[k].title; }).join(', ')));
    }

    if (p === 'fresh') {
      panel.appendChild(el('p', 'fsk-quiz__links',
        '<a class="fsk-link" href="' + DATA.urls.markets + '">Come see us at the booth</a> &middot; ' +
        '<a class="fsk-link" href="' + DATA.urls.recipes + '">Recipes</a>'));
    } else if (p === 'combined') {
      panel.appendChild(el('p', 'fsk-quiz__links',
        '<a class="fsk-link" href="' + DATA.urls.fresh + '">Browse the fresh mushrooms</a> &middot; ' +
        '<a class="fsk-link" href="' + DATA.urls.markets + '">Come see us at the booth</a> &middot; ' +
        '<a class="fsk-link" href="' + DATA.urls.recipes + '">Recipes</a>'));
    }

    // Persistent escape + start over. The "compare the mushrooms" link is dropped for launch:
    // no comparison page exists (ruled 2026-07-19).
    var esc_ = el('p', 'fsk-quiz__escape',
      'Not quite? <a class="fsk-link" href="' + DATA.urls.extracts + '">See all the extracts</a>.');
    panel.appendChild(esc_);

    var over = el('button', 'fsk-quiz__startover', 'Start over');
    over.type = 'button';
    over.addEventListener('click', function () {
      state = { goals: [], take: null, exp: null, job: null, flavor: null, screen: 'p', path: null, r: {}, addons: [], sent: false };
      remember();
      render();
    });
    panel.appendChild(over);

    app.appendChild(panel);
    track('quiz_complete', { path: p, goals: state.goals.join(','), exp: state.exp || '', result: products.join(',') });
    clarityTag('quiz_result', products.length ? products.join('+') : 'fresh');
  }

  // ── Forms ────────────────────────────────────────────────────────────────
  function formFor(kind, resultLabel) {
    var tpl = document.querySelector('[data-quiz-form="' + kind + '"]');
    var wrap = el('div', 'fsk-quiz__formwrap');
    if (!tpl) return wrap;
    wrap.appendChild(tpl.content.cloneNode(true));
    if (state.sent) {
      // After the post: say it went, in the form's own ruled words, instead of a second empty form.
      var ok = wrap.querySelector('.fsk-quiz__ok');
      var msg = ok ? ok.textContent : "Thank you. It's on the way.";
      wrap.innerHTML = '<p class="fsk-quiz__ok" role="status">' + esc(msg) + '</p>';
      return wrap;
    }
    if (kind === 'result' && SETTINGS.klaviyoListId) klaviyoMode(wrap);
    setField(wrap, 'result', resultLabel || '');
    setField(wrap, 'goals', state.goals.join(','));
    setField(wrap, 'path', path());
    setField(wrap, 'take', state.take || '');
    return wrap;
  }
  // Klaviyo subscribe (2026-10-07, Andrew N3; list + one-email flow built by window U-klav-quizflow).
  // Off until the section setting klaviyo_list_id is filled in. Property contract agreed with that
  // window: quiz_path, quiz_result (store handles), quiz_take. ⛔ No rating value is ever sent.
  var KLAVIYO_COMPANY = 'SfGUbD'; // public site id, the same one the onsite script loads
  var RESULT_HANDLES = { lm: 'lions-mane-ultrasound-extract', reishi: 'reishi-ultrasound-extract', trifecta: 'trifecta-ultrasound-extract', daynight: 'day-night-shroom-stack' };
  var FRESH_HANDLES = { lm: 'fresh-lions-mane-mushroom', oyster: 'fresh-oyster-mushrooms', pioppino: 'pioppino' };
  function quizProperties() {
    var prods = path() === 'fresh' ? [] : resolveProducts(state.goals);
    var handles = prods.map(function (k) { return RESULT_HANDLES[k]; });
    if (path() === 'fresh') handles.push(FRESH_HANDLES[state.flavor ? byId(FLAVORS, state.flavor).fresh : 'lm']);
    else if (path() === 'combined') handles.push(FRESH_HANDLES[has(state.goals, 'focus') ? 'lm' : 'oyster']);
    var props = { quiz_path: state.path || (path() === 'combined' ? 'both' : path()), quiz_result: handles.filter(Boolean).join(',') };
    if (path() !== 'fresh' && state.take && !(prods.length === 1 && prods[0] === 'trifecta')) props.quiz_take = state.take;
    return props;
  }
  function klaviyoMode(wrap) {
    var form = wrap.querySelector('form');
    if (!form) return;
    var lede = wrap.querySelector('.fsk-quiz__formlede');
    if (lede) lede.textContent = "Want this saved to your inbox? I'll send your result now.";
    var micro = wrap.querySelector('.fsk-quiz__micro');
    if (micro) micro.textContent = 'One email with your result, then the occasional note from the farm. Unsubscribe anytime.';
    form.addEventListener('submit', function (e) {
      var email = (form.querySelector('input[type="email"]') || {}).value || '';
      if (!email) return; // the browser's own required check speaks
      e.preventDefault();
      var btn = form.querySelector('button[type="submit"], input[type="submit"]');
      if (btn) btn.disabled = true;
      var body = { data: { type: 'subscription', attributes: { profile: { data: { type: 'profile', attributes: { email: email, properties: quizProperties() } } } },
        relationships: { list: { data: { type: 'list', id: SETTINGS.klaviyoListId } } } } };
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY, {
        method: 'POST', headers: { 'Content-Type': 'application/json', revision: '2024-10-15' }, body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) throw new Error('klaviyo ' + r.status);
        wrap.innerHTML = '<p class="fsk-quiz__ok" role="status">Sent. Check your inbox in a minute.</p>';
        track('quiz_email', { kind: 'result' });
        clarityTag('quiz_email', 'sent');
      }).catch(function () {
        // Never lose an address: fall back to the Shopify contact form this box used before.
        if (btn) btn.disabled = false;
        HTMLFormElement.prototype.submit.call(form);
      });
    });
  }
  function setField(scope, name, value) {
    var f = scope.querySelector('[data-quiz-field="' + name + '"]');
    if (f) f.value = value;
  }

  // ── URL state ────────────────────────────────────────────────────────────
  function syncUrl() {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams();
    if (state.goals.length) params.set('goals', state.goals.join(','));
    if (state.take) params.set('take', state.take);
    if (state.exp) params.set('exp', state.exp);
    if (state.job) params.set('job', state.job);
    if (state.flavor) params.set('flavor', state.flavor);
    var ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) params.set('ref', ref);
    var q = params.toString();
    window.history.replaceState({ fskQuiz: state.screen }, '', q ? '?' + q : window.location.pathname);
  }

  // Unknown or conflicting params fall back to the intro rather than erroring.
  function readUrl() {
    var params = new URLSearchParams(window.location.search);
    var saved = recall();
    // Restore this tab's ratings only when they belong to the result in the address (a teaser
    // deep link in a tab that took the quiz earlier must not print old answers).
    var urlGoals = (params.get('goals') || '');
    if (saved && saved.r && (!urlGoals || (saved.goals || []).join(',') === urlGoals)) { state.r = saved.r; state.addons = saved.addons || []; state.path = saved.path || null; }
    // A Shopify contact form posts and reloads this page with ?contact_posted=true and none of the
    // quiz answers, which used to drop the shopper back on a blank quiz with no confirmation.
    // Rebuild the last result from this tab's memory and say it was sent.
    if (/[?&]contact_posted=true/.test(window.location.search) && saved && saved.goals && saved.goals.length) {
      state.goals = saved.goals; state.take = saved.take || null; state.job = saved.job || null; state.flavor = saved.flavor || null;
      state.sent = true; state.screen = 'result';
      return true;
    }
    var goals = (params.get('goals') || '').split(',').filter(function (g) {
      return g && GOALS.some(function (x) { return x.id === g; });
    });
    if (!goals.length) return false;
    state.goals = goals;
    var take = params.get('take');
    state.take = TAKES.some(function (t) { return t.id === take; }) ? take : null;
    var exp = params.get('exp');
    state.exp = EXPERIENCE.some(function (x) { return x.id === exp; }) ? exp : null;
    var job = params.get('job');
    state.job = JOBS.some(function (j) { return j.id === job; }) ? job : null;
    var flavor = params.get('flavor');
    state.flavor = FLAVORS.some(function (f) { return f.id === flavor; }) ? flavor : null;

    if (!state.path) state.path = has(goals, 'cooking') ? (goals.length > 1 ? 'both' : 'fresh') : 'extract';
    if (path() === 'fresh' && !state.flavor) { state.screen = 'q2f'; return true; }
    var resolved = resolveProducts(goals);
    var trifectaOnly = resolved.length === 1 && resolved[0] === 'trifecta'; // its take screen is skipped
    if (path() !== 'fresh' && !state.take && !trifectaOnly) { state.screen = 'q2'; return true; }
    if (askExperience() && !state.exp) { state.screen = 'q3'; return true; }
    state.screen = 'result';
    return true;
  }

  function cameFrom(productUrl) {
    var ref = document.referrer || '';
    return ref.indexOf(productUrl) !== -1;
  }
  function refParam() {
    var g = benefitGoals()[0] || (has(state.goals, 'cooking') ? 'fresh' : 'quiz');
    return '?ref=quiz-' + g;
  }

  // ── Misc ─────────────────────────────────────────────────────────────────
  function byId(list, id) { return list.filter(function (x) { return x.id === id; })[0]; }
  function has(arr, v) { return arr.indexOf(v) !== -1; }
  function track(name, detail) {
    document.dispatchEvent(new CustomEvent('fsk:' + name, { detail: detail }));
    if (window.dataLayer && window.dataLayer.push) {
      window.dataLayer.push(Object.assign({ event: 'fsk_' + name }, detail || {}));
    }
  }
  // Microsoft Clarity custom tags, so quiz sessions can be filtered by step, result and
  // add-to-cart in the Clarity UI. A no-op when Clarity is not on the page.
  function clarityTag(key, value) {
    try { if (typeof window.clarity === 'function') window.clarity('set', key, String(value)); } catch (e) { /* never block the quiz */ }
  }

  // ── Boot ─────────────────────────────────────────────────────────────────
  try {
    // Read the test flag BEFORE the first render: syncUrl() rewrites the query string.
    var debugWanted = /[?&]fskdebug=1/.test(window.location.search);
    var deepLinked = readUrl();
    render();
    track('quiz_start', { deep_linked: deepLinked });
    // Test hook, read-only, only when asked for in the URL: lets a harness drive the real
    // resolver and line selection on the preview without touching any shopper's session.
    if (debugWanted) {
      window.__fskQuiz = {
        resolve: resolveProducts,
        score: goalsFromRatings,
        props: function () { return quizProperties(); },
        setState: function (s) { Object.keys(s).forEach(function (k) { state[k] = s[k]; }); render(); },
        getState: function () { return JSON.parse(JSON.stringify(state)); },
        experienceLine: function (key) { return experienceLine(key, DATA.products[key], DATA.buy && DATA.buy[key]); },
        data: DATA
      };
    }
  } catch (e) {
    // Leave the server-rendered mapping in place.
    app.hidden = true;
    if (staticBlock) staticBlock.hidden = false;
  }
})();
